import React from "react";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Button } from "../components/ui/button";
import { zoneSurfaces, emptyVertical } from "../lib/surfaces";
import { Plus, Trash2, Pencil } from "lucide-react";

const Field = ({ label, testid, value, onChange, onCommit, step = "0.1", suffix = "m" }) => (
  <label className="block">
    <span className="label-xs text-muted-foreground">{label}</span>
    <span className="mt-1 flex items-center gap-1">
      <Input
        data-testid={testid}
        type="number"
        min="0"
        step={step}
        inputMode={step === "1" ? "numeric" : "decimal"}
        value={value}
        onFocus={(e) => {
          if (String(e.target.value) === "0") e.target.select();
        }}
        onChange={(e) => onChange(e.target.value)}
        onBlur={(e) => onCommit?.(e.target.value)}
        className="rounded-none font-mono-t text-sm"
      />
      <span className="label-xs w-6 text-muted-foreground">{suffix}</span>
    </span>
  </label>
);

const Result = ({ label, value, unit, testid, manual }) => (
  <div data-testid={testid}>
    <div className="label-xs text-muted-foreground">{label}</div>
    <div className="font-mono-t text-lg font-bold text-[#1B2A20]">
      {value.toLocaleString("it-IT", { maximumFractionDigits: 2 })} {unit}
    </div>
    {manual && (
      <div className="label-xs flex items-center gap-1 text-[#B45309]">
        <Pencil className="h-3 w-3" /> corretta manualmente
      </div>
    )}
  </div>
);

export const ZoneMeasures = ({ zone, verticalTypes, zoneImages = {}, onChange, onRename, onImageOverride }) => {
  const d = zone.dims;
  const surf = zoneSurfaces(zone);
  // durante la digitazione il campo può restare vuoto: si azzera solo al blur
  const setNum = (k) => (v) => onChange({ ...d, [k]: v === "" ? "" : Number(v) });
  const commitNum = (k) => (v) => {
    if (v === "") onChange({ ...d, [k]: 0 });
  };
  const setRaw = (k) => (v) => onChange({ ...d, [k]: v });

  const setVertical = (id, patch) =>
    onChange({
      ...d,
      verticals: d.verticals.map((v) => (v.id === id ? { ...v, ...patch } : v)),
    });

  return (
    <div className="border border-border bg-card" data-testid="zone-measures">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4 hatch">
        <div>
          <div className="label-xs text-muted-foreground">
            {zone.kind === "internal" ? "Ambiente interno" : "Area esterna"} · {zone.type}
          </div>
          <input
            data-testid="zone-name-input"
            value={zone.name}
            onChange={(e) => onRename(e.target.value)}
            className="w-full border border-transparent bg-transparent font-display text-xl font-semibold uppercase tracking-tight outline-none hover:border-border focus:border-[#4E7C59]"
          />
        </div>
        {Object.keys(zoneImages).length > 0 && (
          <label className="block">
            <span className="label-xs text-muted-foreground">Foto riquadro</span>
            <select
              data-testid="zone-image-select"
              value={zone.imageOverrideType || ""}
              onChange={(e) => onImageOverride(e.target.value || null)}
              className="mt-1 block border border-border bg-background px-2 py-1 text-sm outline-none focus:border-[#4E7C59]"
            >
              <option value="">
                {zoneImages[zone.type] ? `Foto tipologia (${zone.type})` : "Nessuna foto"}
              </option>
              {Object.keys(zoneImages).map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <div className="grid gap-4 px-5 py-4 sm:grid-cols-3">
        <Field label="Lunghezza" testid="zone-length" value={d.length} onChange={setNum("length")} onCommit={commitNum("length")} />
        <Field label="Larghezza" testid="zone-width" value={d.width} onChange={setNum("width")} onCommit={commitNum("width")} />
        {zone.kind === "internal" && (
          <Field label="Altezza" testid="zone-height" value={d.height} onChange={setNum("height")} onCommit={commitNum("height")} />
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 border-t border-border bg-[#F3F6F1] px-5 py-4 sm:grid-cols-4">
        {zone.kind === "internal" ? (
          <>
            <Result label="Pavimento" value={surf.floor} unit="m²" testid="surface-floor" manual={surf.manual.floor} />
            <Result label="Pareti" value={surf.walls} unit="m²" testid="surface-walls" manual={surf.manual.walls} />
            <Result label="Soffitto" value={surf.ceiling} unit="m²" testid="surface-ceiling" manual={surf.manual.floor} />
            <Result label="Perimetro" value={surf.perimeter} unit="ml" testid="surface-perimeter" manual={surf.manual.perimeter} />
          </>
        ) : (
          <>
            <Result label="Superficie" value={surf.area} unit="m²" testid="surface-area" manual={surf.manual.area} />
            <Result label="Perimetro" value={surf.perimeter} unit="ml" testid="surface-perimeter" manual={surf.manual.perimeter} />
            <Result label="Superfici verticali" value={surf.vertical} unit="m²" testid="surface-vertical" manual={surf.manual.vertical} />
          </>
        )}
      </div>

      {zone.kind === "internal" && (
        <div className="border-t border-border px-5 py-4">
          <label className="flex items-center gap-3">
            <Switch
              data-testid="subtract-openings-switch"
              checked={d.subtractOpenings}
              onCheckedChange={(v) => onChange({ ...d, subtractOpenings: v })}
            />
            <span className="text-sm">Sottrarre porte e finestre dalle pareti</span>
          </label>
          {d.subtractOpenings && (
            <div className="mt-4 space-y-4">
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="N° porte" testid="doors-count" value={d.doors} onChange={setNum("doors")} onCommit={commitNum("doors")} step="1" suffix="pz" />
                <Field label="Larghezza porta" testid="door-width" value={d.doorW} onChange={setNum("doorW")} onCommit={commitNum("doorW")} />
                <Field label="Altezza porta" testid="door-height" value={d.doorH} onChange={setNum("doorH")} onCommit={commitNum("doorH")} />
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="N° finestre" testid="windows-count" value={d.windows} onChange={setNum("windows")} onCommit={commitNum("windows")} step="1" suffix="pz" />
                <Field label="Larghezza finestra" testid="window-width" value={d.winW} onChange={setNum("winW")} onCommit={commitNum("winW")} />
                <Field label="Altezza finestra" testid="window-height" value={d.winH} onChange={setNum("winH")} onCommit={commitNum("winH")} />
              </div>
              <div className="grid grid-cols-3 gap-3 border-t border-border pt-3 text-sm">
                <div>
                  <div className="label-xs text-muted-foreground">Pareti lorde</div>
                  <div className="font-mono-t">{surf.wallsGross} m²</div>
                </div>
                <div>
                  <div className="label-xs text-muted-foreground">Aperture</div>
                  <div className="font-mono-t text-destructive">− {surf.openings} m²</div>
                </div>
                <div>
                  <div className="label-xs text-muted-foreground">Pareti nette</div>
                  <div className="font-mono-t font-bold text-[#4E7C59]">{surf.walls} m²</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {zone.kind === "external" && (
        <div className="border-t border-border px-5 py-4">
          <label className="flex items-start gap-3">
            <Switch
              data-testid="has-verticals-switch"
              checked={d.hasVerticals}
              onCheckedChange={(v) =>
                onChange({
                  ...d,
                  hasVerticals: v,
                  verticals: v && d.verticals.length === 0 ? [emptyVertical()] : d.verticals,
                })
              }
            />
            <span className="text-sm">
              Sono presenti pareti, parapetti o altre superfici verticali da lavorare?
            </span>
          </label>

          {d.hasVerticals && (
            <div className="mt-4 space-y-3" data-testid="verticals-list">
              <div className="label-xs text-[#4E7C59]">Superfici verticali</div>
              {d.verticals.map((v, i) => (
                <div
                  key={v.id}
                  className="grid gap-3 border border-border bg-[#F3F6F1] p-3 sm:grid-cols-[1fr_120px_120px_110px_auto] sm:items-end"
                >
                  <label className="block">
                    <span className="label-xs text-muted-foreground">Tipo</span>
                    <select
                      data-testid={`vertical-${i}-type`}
                      value={v.type}
                      onChange={(e) => setVertical(v.id, { type: e.target.value })}
                      className="mt-1 w-full border border-border bg-background px-2 py-2 text-sm outline-none focus:border-[#4E7C59]"
                    >
                      {verticalTypes.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </label>
                  <Field
                    label="Lunghezza"
                    testid={`vertical-${i}-length`}
                    value={v.length}
                    onChange={(val) => setVertical(v.id, { length: val === "" ? "" : Number(val) })}
                    onCommit={(val) => val === "" && setVertical(v.id, { length: 0 })}
                  />
                  <Field
                    label="Altezza"
                    testid={`vertical-${i}-height`}
                    value={v.height}
                    onChange={(val) => setVertical(v.id, { height: val === "" ? "" : Number(val) })}
                    onCommit={(val) => val === "" && setVertical(v.id, { height: 0 })}
                  />
                  <div>
                    <div className="label-xs text-muted-foreground">Superficie</div>
                    <div
                      data-testid={`vertical-${i}-area`}
                      className="font-mono-t text-sm font-bold text-[#4E7C59]"
                    >
                      {(Number(v.length) * Number(v.height)).toLocaleString("it-IT", {
                        maximumFractionDigits: 2,
                      })}{" "}
                      m²
                    </div>
                  </div>
                  <Button
                    data-testid={`vertical-${i}-remove`}
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      onChange({ ...d, verticals: d.verticals.filter((x) => x.id !== v.id) })
                    }
                    className="rounded-none"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              <Button
                data-testid="add-vertical-btn"
                onClick={() => onChange({ ...d, verticals: [...d.verticals, emptyVertical()] })}
                className="rounded-none bg-[#4E7C59] text-white hover:bg-[#1B2A20]"
              >
                <Plus className="mr-2 h-4 w-4" /> Aggiungi superficie verticale
              </Button>
            </div>
          )}
        </div>
      )}

      <details className="border-t border-border px-5 py-4">
        <summary className="cursor-pointer label-xs text-[#4E7C59]">
          Correggi manualmente misure
        </summary>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          {zone.kind === "internal" ? (
            <>
              <Field label="Pavimento" testid="override-floor" value={d.overrideFloor} onChange={setRaw("overrideFloor")} suffix="m²" />
              <Field label="Pareti" testid="override-walls" value={d.overrideWalls} onChange={setRaw("overrideWalls")} suffix="m²" />
              <Field label="Perimetro" testid="override-perimeter" value={d.overridePerimeter} onChange={setRaw("overridePerimeter")} suffix="ml" />
            </>
          ) : (
            <>
              <Field label="Superficie" testid="override-area" value={d.overrideArea} onChange={setRaw("overrideArea")} suffix="m²" />
              <Field label="Perimetro" testid="override-perimeter" value={d.overridePerimeter} onChange={setRaw("overridePerimeter")} suffix="ml" />
              <Field label="Superfici verticali" testid="override-vertical" value={d.overrideVertical} onChange={setRaw("overrideVertical")} suffix="m²" />
            </>
          )}
        </div>
        <p className="mt-2 label-xs text-muted-foreground">
          Lascia vuoto per usare il calcolo automatico.
        </p>
      </details>
    </div>
  );
};
